import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { Card, Button, Space, Typography, Alert, Spin, App, Divider, Tag, Descriptions, List, Input, Avatar, Row, Col, Tooltip, Select, Checkbox, Table, Timeline } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, UserOutlined, FileTextOutlined, MessageOutlined, EditOutlined, PlusOutlined, CloseOutlined, BulbOutlined, OrderedListOutlined, AuditOutlined, TeamOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../utils/config';
import { trIncludes } from '../../utils/trText';
const { Title, Text, Paragraph } = Typography;

const parseMeetingSummary = (summaryText) => {
    if (!summaryText) return null;

    const result = {
        title: '',
        mainSummary: [],
        decisions: [],
        actionItems: []
    };

    const lines = summaryText.split('\n');
    let currentSection = 'main';
    let tableStarted = false;
    let tableHeaders = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        if (line.includes('**TOPLANTI ÖZETİ**') || line === 'TOPLANTI ÖZETİ') {
            result.title = 'Toplantı Özeti';
            continue;
        }

        if (line.includes('**ALINAN KARARLAR**') || line === 'ALINAN KARARLAR') {
            currentSection = 'decisions';
            continue;
        }

        if (line.includes('**AKSİYON MADDELERİ**') || line === 'AKSİYON MADDELERİ') {
            currentSection = 'actions';
            continue;
        }

        if (currentSection === 'actions' && line.startsWith('|')) {
            if (!tableStarted) {
                tableHeaders = line.split('|').filter(h => h.trim()).map(h => h.trim());
                tableStarted = true;
                continue;
            }
            
            if (line.includes('---')) continue;
            const cells = line.split('|').filter(c => c.trim()).map(c => c.trim());
            if (cells.length >= 4) {
                result.actionItems.push({
                    no: cells[0],
                    action: cells[1],
                    responsible: cells[2],
                    description: cells[3] || ''
                });
            }
            continue;
        }

        if (currentSection === 'main') {
            const cleanedLine = line.replace(/^\*\s*\*\*/, '').replace(/\*\*$/, '').replace(/^\*\s*/, '');
            if (cleanedLine) {
                result.mainSummary.push(cleanedLine);
            }
        } else if (currentSection === 'decisions') {
            const decisionMatch = line.match(/^(\d+)\.\s*(.*)$/);
            if (decisionMatch) {
                result.decisions.push({
                    number: decisionMatch[1],
                    text: decisionMatch[2]
                });
            }
        }
    }

    return result;
};

const MeetingSummaryDisplay = ({ summary, darkMode }) => {
    const parsedSummary = useMemo(() => parseMeetingSummary(summary), [summary]);

    if (!parsedSummary) {
        return (
            <Card type="inner" title="Yönetici Özeti">
                <Text type="secondary">Özet bulunamadı.</Text>
            </Card>
        );
    }

    const actionColumns = [
        {
            title: 'No',
            dataIndex: 'no',
            key: 'no',
            width: 60,
            render: (text) => (
                <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>{text}</Tag>
            )
        },
        {
            title: 'Aksiyon Maddesi',
            dataIndex: 'action',
            key: 'action',
            render: (text) => (
                <Text strong style={{ color: darkMode ? 'var(--dark-text)' : '#1a1a1a' }}>{text}</Text>
            )
        },
        {
            title: 'Sorumlu Kişi/Birim',
            dataIndex: 'responsible',
            key: 'responsible',
            width: 180,
            render: (text) => (
                <Tag icon={<TeamOutlined />} color="purple">{text}</Tag>
            )
        },
        {
            title: 'Açıklama',
            dataIndex: 'description',
            key: 'description',
            render: (text) => (
                <Text type="secondary" style={{ fontSize: '13px' }}>{text}</Text>
            )
        }
    ];

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Ana Özet Bölümü */}
            <Card 
                type="inner"
                title={
                    <Space>
                        <div style={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <FileTextOutlined style={{ color: '#fff', fontSize: 18 }} />
                        </div>
                        <div>
                            <Text strong style={{ fontSize: 16, display: 'block' }}>Toplantı Özeti</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>Gündem maddeleri ve tartışılan konular</Text>
                        </div>
                    </Space>
                }
                style={{ 
                    borderRadius: 12,
                    border: darkMode ? '1px solid var(--dark-border)' : '1px solid #e8e8e8',
                    background: darkMode ? 'var(--dark-bg-secondary)' : '#fafbfc'
                }}
                headStyle={{ 
                    borderBottom: darkMode ? '1px solid var(--dark-border)' : '1px solid #e8e8e8',
                    background: darkMode ? 'var(--dark-bg-header)' : '#fff'
                }}
            >
                {parsedSummary.mainSummary.length > 0 && (
                    <Timeline
                        style={{ marginTop: 8 }}
                        items={parsedSummary.mainSummary.map((item, index) => {
                            const cleanText = item.replace(/\*\*/g, '');
                            const colonIndex = cleanText.indexOf(':');
                            const hasTitle = colonIndex > 0 && colonIndex < 60;
                            
                            const titlePart = hasTitle ? cleanText.substring(0, colonIndex) : null;
                            const descPart = hasTitle ? cleanText.substring(colonIndex + 1).trim() : cleanText;
                            
                            return {
                                color: '#52c41a',
                                children: (
                                    <div style={{ 
                                        padding: '8px 12px',
                                        background: darkMode ? 'var(--dark-bg-header)' : '#fff',
                                        borderRadius: 8,
                                        border: darkMode ? '1px solid var(--dark-border)' : '1px solid #f0f0f0',
                                        marginBottom: 4
                                    }}>
                                        {hasTitle ? (
                                            <Text style={{ 
                                                color: darkMode ? 'var(--dark-text)' : '#333',
                                                fontSize: 14,
                                                lineHeight: 1.7
                                            }}>
                                                <Text strong style={{ color: darkMode ? 'var(--dark-text)' : '#1a1a1a' }}>
                                                    {titlePart}:
                                                </Text>{' '}
                                                {descPart}
                                            </Text>
                                        ) : (
                                            <Text style={{ 
                                                color: darkMode ? 'var(--dark-text)' : '#333',
                                                fontSize: 14,
                                                lineHeight: 1.7
                                            }}>
                                                {cleanText}
                                            </Text>
                                        )}
                                    </div>
                                )
                            };
                        })}
                    />
                )}
            </Card>

            {/* Alınan Kararlar Bölümü */}
            {parsedSummary.decisions.length > 0 && (
                <Card 
                    type="inner"
                    title={
                        <Space>
                            <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CheckCircleOutlined style={{ color: '#fff', fontSize: 18 }} />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: 16, display: 'block' }}>Alınan Kararlar</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>Toplantıda alınan önemli kararlar</Text>
                            </div>
                        </Space>
                    }
                    style={{ 
                        borderRadius: 12,
                        border: darkMode ? '1px solid var(--dark-border)' : '1px solid #e8e8e8',
                        background: darkMode ? 'var(--dark-bg-secondary)' : '#fafbfc'
                    }}
                    headStyle={{ 
                        borderBottom: darkMode ? '1px solid var(--dark-border)' : '1px solid #e8e8e8',
                        background: darkMode ? 'var(--dark-bg-header)' : '#fff'
                    }}
                >
                    <List
                        dataSource={parsedSummary.decisions}
                        renderItem={(item, index) => (
                            <List.Item style={{ 
                                padding: '12px 0',
                                borderBottom: index < parsedSummary.decisions.length - 1 
                                    ? (darkMode ? '1px dashed var(--dark-border)' : '1px dashed #e8e8e8')
                                    : 'none'
                            }}>
                                <Space align="start" style={{ width: '100%' }}>
                                    <div style={{
                                        minWidth: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontWeight: 600,
                                        fontSize: 14
                                    }}>
                                        {item.number}
                                    </div>
                                    <Text style={{ 
                                        fontSize: 14, 
                                        lineHeight: 1.7,
                                        color: darkMode ? 'var(--dark-text)' : '#333'
                                    }}>
                                        {item.text}
                                    </Text>
                                </Space>
                            </List.Item>
                        )}
                    />
                </Card>
            )}

            {/* Aksiyon Maddeleri Tablosu */}
            {parsedSummary.actionItems.length > 0 && (
                <Card 
                    type="inner"
                    title={
                        <Space>
                            <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <OrderedListOutlined style={{ color: '#fff', fontSize: 18 }} />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: 16, display: 'block' }}>Aksiyon Maddeleri</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>Yapılacak işler ve sorumlular</Text>
                            </div>
                        </Space>
                    }
                    style={{ 
                        borderRadius: 12,
                        border: darkMode ? '1px solid var(--dark-border)' : '1px solid #e8e8e8',
                        background: darkMode ? 'var(--dark-bg-secondary)' : '#fafbfc'
                    }}
                    headStyle={{ 
                        borderBottom: darkMode ? '1px solid var(--dark-border)' : '1px solid #e8e8e8',
                        background: darkMode ? 'var(--dark-bg-header)' : '#fff'
                    }}
                >
                    <Table
                        columns={actionColumns}
                        scroll={{ x: 'max-content' }}
                        dataSource={parsedSummary.actionItems.map((item, idx) => ({ ...item, key: idx }))}
                        pagination={false}
                        size="middle"
                        className={darkMode ? 'dark-mode-table' : ''}
                        rowClassName={() => darkMode ? 'dark-mode-row' : ''}
                        style={{ 
                            borderRadius: 8,
                            overflow: 'hidden'
                        }}
                    />
                    {darkMode && (
                        <style>{`
                            .dark-mode-table .ant-table {
                                background: var(--dark-bg-secondary) !important;
                            }
                            .dark-mode-table .ant-table-thead > tr > th {
                                background: var(--dark-bg-header) !important;
                                color: var(--dark-text) !important;
                                border-bottom: 1px solid var(--dark-border) !important;
                            }
                            .dark-mode-table .ant-table-tbody > tr > td {
                                background: var(--dark-bg-secondary) !important;
                                color: var(--dark-text) !important;
                                border-bottom: 1px solid var(--dark-border) !important;
                            }
                            .dark-mode-table .ant-table-tbody > tr:hover > td {
                                background: var(--dark-bg-header) !important;
                            }
                            .dark-mode-table .ant-table-cell {
                                color: var(--dark-text) !important;
                            }
                        `}</style>
                    )}
                </Card>
            )}
        </Space>
    );
};

const ToplantiOzetPage = () => {
    const { darkMode } = useTheme();
    const { id: meetingId } = useParams();
    const navigate = useNavigate();
    const { message, notification } = App.useApp();

    const [loading, setLoading] = useState(true);
    const [meetingData, setMeetingData] = useState(null);
    const [speakerMap, setSpeakerMap] = useState({});
    const [transcriptSegments, setTranscriptSegments] = useState([]);
    const [isUpdatingNames, setIsUpdatingNames] = useState(false);
    const [usersList, setUsersList] = useState([]);
    const [sendEmails, setSendEmails] = useState(false);
    const [additionalParticipants, setAdditionalParticipants] = useState([]);

    useEffect(() => {
        const fetchMeetingDetails = async () => {
            if (!meetingId) return;
            setLoading(true);
            const token = localStorage.getItem('auth_token');

            try {
                const response = await fetch(`${API_BASE_URL}/speech2text/meetings/${meetingId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.detail || 'Toplantı detayları alınamadı.');

                setMeetingData(data);

                if (data.speaker_stats) {
                    const initialMap = {};
                    Object.keys(data.speaker_stats).forEach(speakerId => {
                        initialMap[speakerId] = speakerId;
                    });
                    setSpeakerMap(initialMap);
                }

                if (data.corrected_transcript) {
                    const lines = data.corrected_transcript.split('\n').filter(line => line.trim() !== '');
                    const segments = [];
                    lines.forEach(line => {
                        const match = line.match(/^(.*?):\s*(.*)$/);
                        if (match) {
                            segments.push({ speaker: match[1].trim(), text: match[2].trim() });
                        } else if (line.trim()) {
                           segments.push({ speaker: 'Bilinmeyen Konuşmacı', text: line.trim() });
                        }
                    });
                    setTranscriptSegments(segments);
                }

            } catch (error) {
                notification.error({
                    message: 'Hata',
                    description: error.message,
                });
            } finally {
                setLoading(false);
            }
        };

        const fetchUsersList = async () => {
            const token = localStorage.getItem('auth_token');
            try {
                const response = await fetch(`${API_BASE_URL}/speech2text/users/list`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setUsersList(data.users || []);
                }
            } catch (error) {
                console.error('Kullanıcı listesi yükleme hatası:', error);
            }
        };

        fetchMeetingDetails();
        fetchUsersList();
    }, [meetingId, message, notification]);

    const handleSpeakerNameChange = (originalName, userId) => {
        const selectedUser = usersList.find(u => (u.id === userId || u.kullanici_no === userId));
        const newName = selectedUser ? selectedUser.name : originalName;
        setSpeakerMap(prevMap => ({ ...prevMap, [originalName]: newName }));
    };

    const handleAddParticipant = () => {
        setAdditionalParticipants(prev => [...prev, { id: Date.now(), userId: null }]);
    };

    const handleRemoveParticipant = (participantId) => {
        setAdditionalParticipants(prev => prev.filter(p => p.id !== participantId));
    };

    const handleParticipantChange = (participantId, userId) => {
        setAdditionalParticipants(prev => 
            prev.map(p => p.id === participantId ? { ...p, userId } : p)
        );
    };

    const handleUpdateSpeakerNames = async () => {
        if (!meetingData || !meetingData.filename) {
            message.error("Güncellenecek dosya bilgisi bulunamadı.");
            return;
        }
        setIsUpdatingNames(true);
        const token = localStorage.getItem('auth_token');
        try {
            const filenameBase = meetingData.filename.split('.')[0];
            const selectedUserIds = [];
            Object.values(speakerMap).forEach(name => {
                const user = usersList.find(u => u.name === name);
                if (user) {
                    selectedUserIds.push(user.id || user.kullanici_no);
                }
            });
            
            additionalParticipants.forEach(participant => {
                if (participant.userId !== null && participant.userId !== undefined) {
                    selectedUserIds.push(participant.userId);
                }
            });
            
            const uniqueUserIds = [...new Set(selectedUserIds)];
            const response = await fetch(`${API_BASE_URL}/speech2text/update-names`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ 
                    filename_base: filenameBase, 
                    speaker_map: speakerMap,
                    send_emails: sendEmails,
                    summary_content: sendEmails ? meetingData.summary : null,
                    corrected_transcript: sendEmails ? meetingData.corrected_transcript : null,
                    selected_user_ids: sendEmails && uniqueUserIds.length > 0 ? uniqueUserIds : null
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || "İsimler güncellenirken bir hata oluştu.");
            
            let successMsg = "Konuşmacı isimleri başarıyla güncellendi!";
            if (sendEmails && data.emails_count > 0) {
                successMsg += ` ${data.emails_count} kullanıcıya mail gönderildi.`;
            }
            message.success(successMsg);
            
            const updatedSegments = transcriptSegments.map(segment => ({
                ...segment,
                speaker: speakerMap[segment.speaker] || segment.speaker
            }));
            setTranscriptSegments(updatedSegments);

            const newSpeakerMap = {};
            updatedSegments.forEach(segment => {
                if (!newSpeakerMap[segment.speaker]) {
                    newSpeakerMap[segment.speaker] = segment.speaker;
                }
            });
            setSpeakerMap(newSpeakerMap);

        } catch (error) {
            message.error(error.message);
        } finally {
            setIsUpdatingNames(false);
        }
    };

    const stringToColor = (string) => {
        if (!string) return '#1890ff';
        let hash = 0;
        for (let i = 0; i < string.length; i++) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        const hue = Math.abs(hash % 360);
        return `hsl(${hue}, 70%, 45%)`;
    };

    if (loading) {
        return <Spin size="large" style={{ display: 'block', marginTop: '50px', textAlign: 'center' }} />;
    }

    if (!meetingData) {
        return <Alert message="Hata" description="Toplantı verileri yüklenemedi." type="error" showIcon />;
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <Card
                title={
                    <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
                        <Text strong style={{ fontSize: 20 }}>Toplantı Analiz Raporu</Text>
                    </Space>
                }
                extra={
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                        Geri Dön
                    </Button>
                }
                className="meeting-details-card"
            >
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                            <Descriptions.Item label="Toplantı ID">
                                <Tag color="blue">#{meetingData.id}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Dosya Adı">
                                <Text code copyable>{meetingData.filename}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Katılımcı Sayısı">
                                <Tag color="purple">{meetingData.speakers_detected} Kişi</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Konuşma İstatistikleri">
                                <Space wrap>
                                    {meetingData.speaker_stats && Object.entries(meetingData.speaker_stats).map(([id, count]) => (
                                        <Tooltip title={`${count} segment konuşuldu`} key={id}>
                                            <Tag color={stringToColor(speakerMap[id] || id)}>
                                                {speakerMap[id] || id}: {count}
                                            </Tag>
                                        </Tooltip>
                                    ))}
                                </Space>
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>

                    <Col span={24}>
                        <MeetingSummaryDisplay 
                            summary={meetingData.summary} 
                            darkMode={darkMode}
                        />
                    </Col>

                    <Col span={24}>
                        <Divider orientation="left">
                            <Space><UserOutlined /> <Text strong>Katılımcı İsimlerini Düzenle</Text></Space>
                        </Divider>
                        <div style={{ 
                            background: darkMode ? 'var(--dark-bg-secondary)' : '#fff', 
                            padding: '16px', 
                            borderRadius: '8px', 
                            border: darkMode ? '1px solid var(--dark-border)' : '1px solid #f0f0f0' 
                        }}>
                            <Row gutter={[16, 16]} align="middle">
                                {Object.keys(speakerMap).map(originalName => (
                                    <Col xs={24} sm={12} md={8} lg={6} key={originalName}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text-muted)' : '#666' }}>
                                                {originalName}:
                                            </Text>
                                            <Select
                                                style={{ width: '100%' }}
                                                placeholder="Kullanıcı seçin"
                                                showSearch
                                                filterOption={(input, option) =>
                                                    trIncludes(option?.label ?? '', input)
                                                }
                                                options={usersList.map(user => ({
                                                    value: user.id || user.kullanici_no,
                                                    label: user.name,
                                                    key: user.id || user.kullanici_no
                                                }))}
                                                value={(() => {
                                                    const currentName = speakerMap[originalName];
                                                    if (currentName === originalName) return undefined;
                                                    const user = usersList.find(u => u.name === currentName);
                                                    return user ? (user.id || user.kullanici_no) : undefined;
                                                })()}
                                                onChange={(userId) => handleSpeakerNameChange(originalName, userId)}
                                                allowClear
                                            />
                                        </Space>
                                    </Col>
                                ))}
                                <Col xs={24}>
                                    <Divider orientation="left" style={{ marginTop: 24 }}>
                                        <Text strong>Toplantı Katılımcıları</Text>
                                    </Divider>
                                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                        {additionalParticipants.map(participant => (
                                            <Space key={participant.id} style={{ width: '100%' }} align="center">
                                                <Select
                                                    style={{ width: '300px' }}
                                                    placeholder="Katılımcı seçin"
                                                    showSearch
                                                    filterOption={(input, option) =>
                                                        trIncludes(option?.label ?? '', input)
                                                    }
                                                    options={usersList.map(user => ({
                                                        value: user.id || user.kullanici_no,
                                                        label: user.name,
                                                        key: user.id || user.kullanici_no
                                                    }))}
                                                    value={participant.userId}
                                                    onChange={(userId) => handleParticipantChange(participant.id, userId)}
                                                    allowClear
                                                />
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<CloseOutlined />}
                                                    onClick={() => handleRemoveParticipant(participant.id)}
                                                />
                                            </Space>
                                        ))}
                                        <Button
                                            type="dashed"
                                            icon={<PlusOutlined />}
                                            onClick={handleAddParticipant}
                                            style={{ width: '300px' }}
                                        >
                                            Katılımcı Ekle
                                        </Button>
                                    </Space>
                                </Col>
                                <Col xs={24}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Checkbox
                                            checked={sendEmails}
                                            onChange={(e) => setSendEmails(e.target.checked)}
                                        >
                                            Seçilen tüm katılımcılara toplantı özetini mail ile gönder
                                        </Checkbox>
                                        <Button
                                            type="primary"
                                            icon={<EditOutlined />}
                                            onClick={handleUpdateSpeakerNames}
                                            loading={isUpdatingNames}
                                            disabled={isUpdatingNames}
                                        >
                                            İsim Değişikliklerini Kaydet {sendEmails && '(Mail Gönderilecek)'}
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </div>
                    </Col>

                    <Col span={24}>
                        <Divider orientation="left">
                            <Space><MessageOutlined /> <Text strong>Detaylı Konuşma Dökümü</Text></Space>
                        </Divider>
                        
                        <List
                            className="transcript-list"
                            itemLayout="horizontal"
                            dataSource={transcriptSegments}
                            pagination={{
                                onChange: (page) => {
                                    console.log(page);
                                },
                                pageSize: 20,
                                align: 'center',
                                className: darkMode ? 'dark-pagination' : ''
                            }}
                            renderItem={(item) => (
                                <List.Item style={{ padding: '16px', borderBottom: darkMode ? '1px solid var(--dark-border)' : '1px solid #f0f0f0' }}>
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar 
                                                style={{ backgroundColor: stringToColor(item.speaker), verticalAlign: 'middle' }} 
                                                size="large"
                                                icon={<UserOutlined />}
                                            >
                                                {item.speaker.charAt(0).toUpperCase()}
                                            </Avatar>
                                        }
                                        description={
                                            <div style={{ 
                                                marginTop: '8px', 
                                                fontSize: '15px', 
                                                color: darkMode ? 'var(--dark-text)' : '#333', 
                                                lineHeight: '1.6',
                                                backgroundColor: darkMode ? 'var(--dark-bg-header)' : '#fafafa',
                                                padding: '12px',
                                                borderRadius: '0 12px 12px 12px',
                                                display: 'inline-block',
                                                maxWidth: '100%',
                                                border: darkMode ? '1px solid var(--dark-border)' : 'none'
                                            }}>
                                                {item.text}
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default ToplantiOzetPage;